import { useEffect, useState } from "react";
import Organization from "../../models/organization";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import AdminBoxConent from "../../components/admin/AdminBoxContent";
import ProjectCollection from "../../models/projectCollection";
import projectService from "../../services/projectService";
import Project from "../../models/project";
import localStorageService from "../../services/localStorageService";
import dateTimeHelper from "../../util/dateTimeHelper";
import Moment from "react-moment";

const Projects: React.FC<{ organization: Organization | undefined }> = ({
  organization,
}) => {
  const navigate = useNavigate();

  const [projectCollections, setProjectCollections] = useState<
    ProjectCollection[]
  >([]);
  const [allProjectCollections, setAllProjectCollections] = useState<
    ProjectCollection[]
  >([]);

  const [showAllProjects, setShowAllProjects] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");

  // const [isAddNew2Enabled, setIsAddNew2Enabled] = useState<boolean>(true);

  const [isFilterByNoThematicMaps, setIsFilterByNoThematicMaps] =
    useState<boolean>(false);
  const [isFilterByWithThematicMaps, setIsFilterByWithThematicMaps] =
    useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      const { data: allProjectCollections } =
        await projectService.getProjectCollections();
      setAllProjectCollections(allProjectCollections);
    };

    init();
  }, []);

  useEffect(() => {
    if (!organization) {
      return;
    }

    let projectCollections: ProjectCollection[] = [];

    if (
      !showAllProjects &&
      organization.organizationName !== "International Cybernetics"
    ) {
      projectCollections = allProjectCollections.filter((pc) =>
        pc.organizations.find(
          (o) => o.idOrganization === organization.idOrganization,
        ),
      );
    } else {
      projectCollections = allProjectCollections;
    }

    projectCollections = projectCollections.filter(
      (pc) =>
        pc.collectionName.toLowerCase().includes(searchText) ||
        pc.projects.filter(
          (p) =>
            p.projectName.toLowerCase().includes(searchText) ||
            p.year.toLowerCase().includes(searchText) ||
            p.projectCode.toLowerCase().includes(searchText) ||
            p.databaseName.toLowerCase().includes(searchText) ||
            p.reportName.toLowerCase().includes(searchText),
        ).length > 0 ||
        pc.organizations.filter((o) =>
          o.organizationName.toLowerCase().includes(searchText),
        ).length > 0,
    );

    if (isFilterByWithThematicMaps) {
      projectCollections = projectCollections.filter(
        (pc) => pc.projects.filter((p) => p.hasThematicMaps).length > 0,
      );
    } else if (isFilterByNoThematicMaps) {
      projectCollections = projectCollections.filter(
        (pc) => pc.projects.filter((p) => !p.hasThematicMaps).length > 0,
      );
    }

    setProjectCollections(projectCollections);
  }, [
    allProjectCollections,
    organization,
    showAllProjects,
    searchText,
    isFilterByNoThematicMaps,
    isFilterByWithThematicMaps,
  ]);

  const onShowAllProjectsChanged = () => {
    const newValue = !showAllProjects;
    setShowAllProjects(newValue);

    setSearchText("");
  };

  const onSearchTextChange = (event: any) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value);
  };

  const setIsFilterByNoThematicMapsCheckboxChecked = (event: any) => {
    const checked: boolean = event.target.checked;
    setIsFilterByNoThematicMaps(checked);

    setIsFilterByWithThematicMaps(false);
  };

  const setIsFilterByWithThematicMapsCheckboxChecked = (event: any) => {
    const checked: boolean = event.target.checked;
    setIsFilterByWithThematicMaps(checked);

    setIsFilterByNoThematicMaps(false);
  };

  const onProjectCollectionClick = (projectCollection: ProjectCollection) => {
    localStorageService.setIdProjectCollection(
      projectCollection.idProjectCollection,
    );
    navigate("/");
  };

  const onProjectClick = (
    project: Project,
    projectCollection: ProjectCollection,
  ) => {
    localStorageService.setIdProjectCollection(
      projectCollection.idProjectCollection,
    );
    localStorageService.setIdProject(project.idProject);
    navigate("/");
  };

  const onNewProjectCollection = () => {
    navigate("/admin/project-collections/new");
  };

  const onEditProjectCollection = (projectCollection: ProjectCollection) => {
    navigate(
      `/admin/project-collections/${projectCollection.idProjectCollection}`,
    );
  };

  const onNewProject = () => {
    navigate("/admin/projects/new");
  };

  const onEditProject = (project: Project) => {
    navigate(`/admin/projects/${project.idProject}`);
  };

  let projectCount = 0;
  for (const projectCollection of projectCollections) {
    projectCount += projectCollection.projects.length;
  }

  //const hasAddNew = organizationService.isIccOrganization(organization);
  const hasAddNew = false;

  return (
    <AdminBoxConent
      title="Projects"
      hasAddNew={hasAddNew}
      addNewTitle="Add New Project Collection"
      onAddNew={() => onNewProjectCollection()}
      addNew2Title="Add New Project"
      onAddNew2={() => onNewProject()}
      // isAddNew2Enabled={isAddNew2Enabled}
      isAddNew2Enabled={false}
      count={projectCount}
      countType="projects"
      hasCurrentOrganizationDropdown={!showAllProjects}
    >
      <div className="mb-3">
        <div className="d-flex align-items-center">
          <label className="form-check-label" htmlFor="showAllProjects">
            Show All Projects
          </label>
          <div className="form-switch form-switch-right-text">
            <input
              className="form-check-input form-switch-right-text"
              type="checkbox"
              checked={showAllProjects}
              onChange={onShowAllProjectsChanged}
              id="showAllProjects"
            />
          </div>
        </div>
      </div>
      <div className="mb-3">
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            placeholder="Search"
            value={searchText}
            onChange={onSearchTextChange}
          />
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </div>
      </div>
      <div className="mb-2 d-flex align-items-center">
        <div className="me-2">Filter</div>
        <div className="btn-group me-3">
          <input
            type="checkbox"
            checked={isFilterByNoThematicMaps}
            onChange={(e) => setIsFilterByNoThematicMapsCheckboxChecked(e)}
            className="btn-check"
            id="btncheck_isFilterByNoThematicMaps"
            autoComplete="off"
          />
          <label
            className="btn btn-outline-primary btn-sm"
            htmlFor="btncheck_isFilterByNoThematicMaps"
          >
            No Thematic Maps
          </label>
          <input
            type="checkbox"
            checked={isFilterByWithThematicMaps}
            onChange={(e) => setIsFilterByWithThematicMapsCheckboxChecked(e)}
            className="btn-check"
            id="btncheck_isFilterByWithThematicMaps"
            autoComplete="off"
          />
          <label
            className="btn btn-outline-primary btn-sm"
            htmlFor="btncheck_isFilterByWithThematicMaps"
          >
            With Thematic Maps
          </label>
        </div>
      </div>
      <table className="table table-hover">
        <thead>
          <tr>
            <th scope="col">Collection</th>
            <th scope="col">Project</th>
            <th scope="col">Year</th>
            <th scope="col">Project Code</th>
            <th scope="col">Organization</th>
            <th scope="col">Has Thematic Maps</th>
            <th scope="col">Has Distress</th>
            <th scope="col">Has Default Location</th>
            <th scope="col">Has Default Layout</th>
            <th scope="col">Database</th>
            <th scope="col">Report</th>
            <th scope="col">Interval</th>
            <th scope="col">Locators [DisplayName: FieldName]</th>
            <th scope="col">Networks</th>
            <th scope="col">ESA Measurement Date</th>
            <th scope="col">Is Online</th>
            <th style={{ width: "50px" }} />
          </tr>
        </thead>
        <tbody>
          {projectCollections.map(
            (projectCollection: ProjectCollection, collectionIndex) => {
              if (projectCollection.projects.length === 0) {
                return (
                  <tr
                    key={
                      "projectCollection_project" + collectionIndex.toString()
                    }
                  >
                    {/* Collection */}
                    <td>
                      <div className="d-flex">
                        <div
                          className="link"
                          onClick={() =>
                            onProjectCollectionClick(projectCollection)
                          }
                        >
                          {projectCollection.collectionName}
                        </div>
                        <div style={{ marginLeft: "5px" }}>
                          <EditIcon
                            style={{ cursor: "pointer" }}
                            fontSize="small"
                            onClick={() =>
                              onEditProjectCollection(projectCollection)
                            }
                          />
                        </div>
                      </div>
                    </td>
                    {/* Project */}
                    <td />
                    {/* Year */}
                    <td />
                    {/* Project Code */}
                    <td />
                    {/* Organization */}
                    <td>
                      <div key={"organization_" + collectionIndex.toString()}>
                        {projectCollection.organizations
                          .map((o) => o.organizationName)
                          .join(", ")}
                      </div>
                    </td>
                    {/* Has Thematic Maps */}
                    <td />
                    {/* Has Distress */}
                    <td />
                    {/* Has Default Location */}
                    <td />
                    {/* Has Default Layout */}
                    <td />
                    {/* Database */}
                    <td />
                    {/* Report */}
                    <td />
                    {/* Interval */}
                    <td />
                    {/* Locators */}
                    <td />
                    {/* Networks */}
                    <td />
                    {/* ESA Measurement Date */}
                    <td />
                    {/* Is Online */}
                    <td />
                  </tr>
                );
              } else {
                return projectCollection.projects.map(
                  (project: Project, projectIndex) => (
                    <tr
                      key={
                        "projectCollection_project" +
                        collectionIndex.toString() +
                        projectIndex.toString()
                      }
                    >
                      <td>
                        {projectIndex === 0 && (
                          <div className="d-flex">
                            <div
                              className="link"
                              onClick={() =>
                                onProjectCollectionClick(projectCollection)
                              }
                            >
                              {projectCollection.collectionName}
                            </div>
                            <div style={{ marginLeft: "5px" }}>
                              <EditIcon
                                style={{ cursor: "pointer" }}
                                fontSize="small"
                                onClick={() =>
                                  onEditProjectCollection(projectCollection)
                                }
                              />
                            </div>
                          </div>
                        )}
                      </td>
                      <td>
                        <div
                          key={"projectName_" + projectIndex.toString()}
                          className="link"
                          onClick={() =>
                            onProjectClick(project, projectCollection)
                          }
                        >
                          {project.projectName}
                        </div>
                      </td>
                      <td>
                        <div key={"projectYear_" + projectIndex.toString()}>
                          {project.year}
                        </div>
                      </td>
                      <td>
                        <div key={"projectCode_" + projectIndex.toString()}>
                          {project.projectCode}
                        </div>
                      </td>
                      <td>
                        <div key={"organization_" + projectIndex.toString()}>
                          {projectCollection.organizations
                            .map((o) => o.organizationName)
                            .join(", ")}
                        </div>
                      </td>
                      <td>
                        <div
                          key={
                            "projectHasThematicMaps_" + projectIndex.toString()
                          }
                        >
                          {project.hasThematicMaps ? "True" : "False"}
                        </div>
                      </td>
                      <td>
                        <div
                          key={"projectHasDistress_" + projectIndex.toString()}
                        >
                          {project.hasDistress ? "True" : "False"}
                        </div>
                      </td>
                      <td>
                        <div
                          key={
                            "defaultWorkspaceLayout_" + projectIndex.toString()
                          }
                        >
                          {project.defaultLocation ? "True" : "False"}
                        </div>
                      </td>
                      <td>
                        <div
                          key={
                            "defaultWorkspaceLayout_" + projectIndex.toString()
                          }
                        >
                          {project.defaultWorkspaceLayout ? "True" : "False"}
                        </div>
                      </td>
                      <td>
                        <div key={"databaseName_" + projectIndex.toString()}>
                          {project.databaseName}
                        </div>
                      </td>
                      <td>
                        <div key={"reportName_" + projectIndex.toString()}>
                          {project.reportName}
                        </div>
                      </td>
                      <td>
                        <div key={"reportInterval_" + projectIndex.toString()}>
                          {project.reportInterval}
                        </div>
                      </td>
                      <td>
                        <div key={"locators_" + projectIndex.toString()}>
                          {project.projectLocatorFields
                            .map((l) => `[${l.displayName}: ${l.fieldName}]`)
                            .join(", ")}
                        </div>
                      </td>
                      <td>
                        <div key={"networks_" + projectIndex.toString()}>
                          {project.projectNetworkLayers
                            .map((nl) => `${nl.networkLayerName}`)
                            .join(", ")}
                        </div>
                      </td>
                      <td>
                        <div
                          key={"esaMeasuementDate_" + projectIndex.toString()}
                        >
                          {project.esaCurrentMeasurementDate && (
                            <Moment format={dateTimeHelper.dateFormat}>
                              {project.esaCurrentMeasurementDate}
                            </Moment>
                          )}
                        </div>
                      </td>
                      <td>
                        <div key={"isOnline_" + projectIndex.toString()}>
                          {project.isOnline ? "True" : "False"}
                        </div>
                      </td>
                      <td>
                        <div key={"projectEdit_" + projectIndex.toString()}>
                          <EditIcon
                            style={{ cursor: "pointer", marginRight: "10px" }}
                            fontSize="small"
                            onClick={() => onEditProject(project)}
                          />
                        </div>
                      </td>
                    </tr>
                  ),
                );
              }
            },
          )}
        </tbody>
      </table>
    </AdminBoxConent>
  );
};
export default Projects;
